import { useEffect, useState } from 'react';

/**
 * Reports which section is currently in view, for highlighting the nav on a
 * single-page site.
 *
 * The root margin biases towards the upper part of the viewport, so the
 * highlighted item is the section being read rather than whichever one happens
 * to touch the bottom edge.
 *
 * The header mounts before the lazily-loaded page it describes, so the
 * sections usually do not exist yet on the first run — hence the retry.
 */
export function useActiveSection(ids: readonly string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    let observer: IntersectionObserver | null = null;
    let frame = 0;
    let attempts = 0;
    const visible = new Map<string, number>();

    const start = () => {
      const elements = ids
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);

      if (elements.length === 0) {
        // Wait for the route's chunk to render, then look again.
        if (attempts < 60) {
          attempts += 1;
          frame = requestAnimationFrame(start);
        }
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              visible.set(entry.target.id, entry.intersectionRatio);
            } else {
              visible.delete(entry.target.id);
            }
          }

          // Whichever tracked section occupies most of the reading band wins.
          let best: string | null = null;
          let bestRatio = -1;
          for (const [id, ratio] of visible) {
            if (ratio > bestRatio) {
              best = id;
              bestRatio = ratio;
            }
          }
          setActiveId(best);
        },
        { rootMargin: '-15% 0px -60% 0px', threshold: 0 },
      );

      for (const element of elements) observer.observe(element);
    };

    start();

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [ids]);

  return activeId;
}
